import React, { Fragment, useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Collapse, TableCell, TableRow, Tooltip } from '@mui/material';
import TextTableCell from 'components/common/table/TextTableCell';
import CheckboxTableCell from 'components/common/table/CheckboxTableCell';
import ActionsTableCell from 'components/common/table/ActionsTableCell';
import IconButton from 'components/common/buttons/IconButton';
import { CityEditCell, CityHeaderRow, HierarchyContainer, HierarchyIcon } from 'features/city/components/Styles';
import FlexBox from 'components/common/FlexBox';
import { getActionsSlotId, getCityTypeIcon } from 'features/city/functions';
import { useTranslation } from 'react-i18next';
import CityEditComponent from './edit/CityEditComponent';
import { hierarchyInEditRootIdVar } from '../variables';
import { Card, Typography } from '@totalsoft/rocket-ui';
import { useMutation } from 'hooks/apollo/useMutation';
import { DELETE_CITIES } from '../mutations';
import useSubscription from 'hooks/apollo/useSubscription';
import { CITIES_DELETED } from '../subscriptions';
import { citiesStore } from '../store';
import { usePermission } from 'providers/RightsHooks';
import { ADMIN_CITIES_WRITE } from 'constants/rightsCodes';
import CityIcon from './CityIcon';

const ItemComponent = ({ data }) => {
  const { t } = useTranslation();

  const canWrite = usePermission(ADMIN_CITIES_WRITE);

  const [expanded, setExpanded] = useState(false);
  const [pending, setPending] = useState(false);

  const toggleExpanded = useCallback(() => setExpanded(prev => !prev), []);

  const hierarchy = useMemo(() => {
    const result = [...(data.parents || [])];
    result.push({ id: data.id, name: data.name, type: data.type, last: true });
    return result;
  }, [data]);

  const handleEditHierarchy = useCallback(() => {
    const rootId = hierarchy[0].id;
    hierarchyInEditRootIdVar(rootId);
  }, [hierarchy]);

  const actionsSlotId = useMemo(getActionsSlotId, []);

  // #region Delete

  const [deleteCities] = useMutation(DELETE_CITIES, {
    onError: _ => {
      setPending(false);
    }
  });

  const handleDelete = useCallback(() => {
    if (!window.confirm(t('City.ConfirmDelete'))) return;

    setPending(true);

    deleteCities({ variables: { ids: [data.id] } });
  }, [deleteCities, data.id, t]);

  useSubscription(CITIES_DELETED, {
    onData: ({
      data: {
        data: {
          citiesDeleted: { ids }
        }
      }
    }) => {
      if (!ids.includes(data.id)) return;
      setPending(false);
      const store = citiesStore.get();
      store.refetch();
    }
  });

  // #endregion

  return (
    <>
      <CityHeaderRow>
        <TableCell>
          <CityIcon type={data.type} />
        </TableCell>
        <TextTableCell content={data.code} />
        <TextTableCell content={data.name} />
        <CheckboxTableCell checked={data.inactive} />
        <ActionsTableCell>
          <IconButton
            icon={canWrite ? 'edit_note' : 'visibility'}
            tooltip={canWrite ? t('City.EditCityHierarchy') : t('City.ViewCityHierarchy')}
            onClick={handleEditHierarchy}
            disabled={pending}
          />
          {canWrite && <IconButton type='delete' color='error' disabled={pending} onClick={handleDelete} />}
          <IconButton type={expanded ? 'expandLess' : 'expandMore'} onClick={toggleExpanded} disabled={pending} />
        </ActionsTableCell>
      </CityHeaderRow>
      <TableRow>
        <CityEditCell colSpan={5}>
          <Collapse in={expanded} mountOnEnter unmountOnExit>
            <Card sx={{ mx: 1, mb: 1.5 }}>
              <FlexBox column>
                <FlexBox justifyContent='space-between' alignItems='center' mt={-1} mb={1}>
                  <Tooltip title={t('City.EditCityHierarchy')}>
                    <HierarchyContainer gap={0.5} alignItems='center'>
                      {hierarchy.map((node, index) => (
                        <FlexBox key={index} alignItems='center' gap={0.5} onClick={handleEditHierarchy}>
                          <HierarchyIcon>{getCityTypeIcon(node.type)}</HierarchyIcon>
                          <Typography title={node.name}>{node.name}</Typography>
                          {!node.last && <HierarchyIcon>navigate_next</HierarchyIcon>}
                        </FlexBox>
                      ))}
                    </HierarchyContainer>
                  </Tooltip>
                  <FlexBox id={actionsSlotId} />
                </FlexBox>
                <CityEditComponent data={data} actionsSlotId={actionsSlotId} />
              </FlexBox>
            </Card>
          </Collapse>
        </CityEditCell>
      </TableRow>
    </>
  );
};

ItemComponent.propTypes = {
  data: PropTypes.object
};

export default ItemComponent;
